import { useEffect, useRef, useState } from "react";
import { createFacilityMap, loadNaverMap, mapAuthFailureEvent, userPositionLabel, type MapPoint, type UserMapPosition } from "./maps/naver.js";
import { InfoDisclosure } from "./InfoDisclosure.js";

export function FacilityMap({ clientId, points, selectedId, onSelect, opened, onOpenedChange, userPosition = null }: {
  clientId: string | null; points: MapPoint[]; selectedId: string | null; onSelect: (id: string) => void;
  opened: boolean; onOpenedChange: (opened: boolean) => void;
  userPosition?: UserMapPosition | null;
}): React.JSX.Element | null {
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<"idle" | "loading" | "rendering" | "ready" | "error">("idle");
  const element = useRef<HTMLDivElement>(null);
  const controller = useRef<ReturnType<typeof createFacilityMap> | null>(null);
  const selection = useRef(onSelect);
  const visible = opened && clientId !== null && (points.length > 0 || userPosition !== null);
  useEffect(() => { selection.current = onSelect; }, [onSelect]);

  useEffect(() => {
    if (!visible || clientId === null || element.current === null) return;
    let active = true;
    let failed = false;
    let observer: ResizeObserver | undefined;
    const fail = (): void => { failed = true; if (active) setState("error"); };
    window.addEventListener(mapAuthFailureEvent, fail);
    void loadNaverMap(clientId).then((sdk) => {
      if (!active || element.current === null) return;
      controller.current = createFacilityMap(element.current, sdk, (id) => selection.current(id), () => { if (active && !failed) setState("ready"); }, fail);
      observer = new ResizeObserver(() => controller.current?.resize());
      observer.observe(element.current);
      setState("rendering");
    }).catch(fail);
    return () => {
      active = false;
      observer?.disconnect();
      controller.current?.destroy(); controller.current = null;
      window.removeEventListener(mapAuthFailureEvent, fail);
    };
  }, [clientId, visible, attempt]);
  useEffect(() => { if (state === "ready" || state === "rendering") controller.current?.update(points, userPosition); }, [points, userPosition, state]);
  useEffect(() => { if (state === "ready") controller.current?.select(selectedId); }, [selectedId, points, state]);

  if (clientId === null || (points.length === 0 && userPosition === null)) return null;
  return <section className="facility-map" aria-label="의료기관 지도">
    <div className="map-heading"><h3>{userPosition !== null ? "내 위치와 주변 기관" : "지도에서 한눈에 보기"}</h3><InfoDisclosure label="지도 이용 안내"><p>지도에 표시할 위치(내 위치 포함)와 접속 정보가 네이버로 전달됩니다. 입력한 주소 원문이나 증상은 보내지 않습니다. 지도 제공자의 데이터 처리 방식이 적용됩니다.</p><p>동의는 이 페이지에서만 유지되며, 지도 끄기로 해제할 수 있습니다. 파란 원은 브라우저가 알려준 위치 오차 범위입니다. 번호를 누르면 해당 기관으로 이동합니다.</p></InfoDisclosure></div>
    {!opened && <div className="map-consent"><p>{userPosition !== null ? "내 위치와 기관 위치를 네이버 지도에 표시합니다." : "기관 위치를 네이버 지도에 표시합니다."} 표시할 위치와 접속 정보가 네이버로 전달됩니다.</p><button type="button" className="map-open" onClick={() => { setState("loading"); onOpenedChange(true); }}>동의하고 지도 보기</button></div>}
    {opened && <><div ref={element} className="map-canvas" aria-label="네이버지도" hidden={state === "error"}/>
      <div className="map-controls" role="group" aria-label="지도 조작">{state === "ready" && <>{userPosition !== null && <button type="button" onClick={() => controller.current?.centerOnUser()}>내 위치로</button>}<button type="button" onClick={() => controller.current?.zoomBy(1)}>+ 확대</button><button type="button" onClick={() => controller.current?.zoomBy(-1)}>− 축소</button></>}<button type="button" onClick={() => { setState("idle"); onOpenedChange(false); }}>지도 끄기</button></div>
      <p className="privacy-note" role="status">{state === "idle" || state === "loading" || state === "rendering" ? "지도를 불러오고 있어요…" : state === "error" ? "지도를 불러오지 못했어요. 목록에서 확인해 주세요." : userPosition !== null ? `파란 점: ${userPositionLabel(userPosition)} · 번호: 의료기관` : "번호를 누르면 기관 정보를 볼 수 있어요."}</p>
      {state === "error" && <button type="button" className="map-open" onClick={() => { setState("loading"); setAttempt((value) => value + 1); }}>지도 다시 불러오기</button>}
    </>}
  </section>;
}
